import { Icon, IconName } from "@zapier/zinnia-icons";
import {
  Heading,
  IconButton,
  SkeletonBlock,
  Tooltip,
} from "@zapier/design-system";
import { useTranslations } from "@zapier/i18n";
import { useFavoriteAssets } from "@zapier/client";
import { useEmitFavoriteEvent, FavoriteOptions } from "@zapier/shared";
import { AssetTypes } from "@zapier/utils";
import style from "./FolderHeaderTitle.module.css";

interface Props {
  title: string;
  folderId?: string;
  isFoldersLoading?: boolean;
  isShared?: boolean;
  isTrashed?: boolean;
  icon?: IconName;
}

const FolderHeaderTitle = ({
  title,
  folderId,
  isFoldersLoading,
  isShared,
  isTrashed = false,
  icon,
}: Props) => {
  const t = useTranslations("table-row");
  const { isFavorite, toggleFavorite } = useFavoriteAssets();
  const { emit: emitFavoriteEvent } = useEmitFavoriteEvent();

  const showFavoriteButton = !!folderId && !isTrashed;
  const isCurrentFavorite =
    showFavoriteButton && isFavorite(folderId, AssetTypes.Folder);

  const handleToggleFavorite = () => {
    if (folderId) {
      emitFavoriteEvent({
        optionSelected: isCurrentFavorite
          ? FavoriteOptions.Remove
          : FavoriteOptions.Add,
        interactionRawText: AssetTypes.Folder,
      });
      toggleFavorite(folderId, AssetTypes.Folder);
    }
  };

  return (
    <div className={style.titleWrapper} data-testid="FolderHeaderTitle">
      <Icon
        name={icon || (isShared ? "team" : "navFolder")}
        className={style.icon}
        size={36}
      />
      {isFoldersLoading ? (
        <span className={style.skeletonWrapper}>
          <SkeletonBlock width={300} height={24} />
        </span>
      ) : (
        <>
          <Heading className={style.heading}>{title}</Heading>
          {showFavoriteButton && (
            <Tooltip
              content={
                isCurrentFavorite
                  ? t("tooltips.remove-from-favorites")
                  : t("tooltips.add-to-favorites")
              }
            >
              <IconButton
                aria-label={
                  isCurrentFavorite
                    ? t("tooltips.remove-from-favorites")
                    : t("tooltips.add-to-favorites")
                }
                className={style.favoriteButton}
                name={isCurrentFavorite ? "ratingStarFill" : "ratingStar"}
                onPress={handleToggleFavorite}
                size="medium"
                variant="ghost"
              />
            </Tooltip>
          )}
        </>
      )}
    </div>
  );
};

export default FolderHeaderTitle;
